import { Tooltip } from 'antd'
import clsx from 'clsx'
import { type ComponentPropsWithoutRef, type FC, useCallback } from 'react'
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form'

import {
  Button,
  NumberFormatter,
  BasicTable as Table,
  BasicTbody as Tbody,
  BasicTd as Td,
  BasicTh as Th,
  BasicTr as Tr,
  UnitLabel
} from '@/components/elements'
import {
  ACTIONS,
  DESCRIPTIONS,
  UNITS
} from '@/constants/common/message.constants'
import { useAdditionalPaymentCategoriesSelectQuery } from '@/features/additional-payment-categories/hooks'
import { REPORT_TYPE } from '@/libs/api/types'

import {
  buildCopiedAdditionalPaymentFormValue,
  createInitialShipmentOrderAdditionalFormValue,
  type ShipmentOrderAdditionalFormValue,
  sumShipmentOrderAdditionalAmounts
} from '../../models/shipment-order-additional-form-value'

import { type FieldArrayName } from './field-array-name'
import { ShipmentOrderAdditionalRow } from './ShipmentOrderAdditionalRow'

type Props = ComponentPropsWithoutRef<'div'> & {
  /**
   * RHF `useFieldArray` の name。
   *   - 単一配送依頼の編集モーダル: `'additionalCharges'` / `'additionalPayments'`
   *   - 配車板 (複数 shipments 配列) の編集モーダル: `'shipments.${index}.additionalCharges'` 等
   */
  fieldArrayName: FieldArrayName
  /** セクションタイトル (例: 請求側付帯費用 / 支払側付帯費用) */
  title: string
  /** 末尾合計表示用ラベル (例: 追加請求額合計) */
  totalLabel: string
  /** 編集可否 */
  isEditing: boolean
  /** テナント key */
  tenantKey: string
  /**
   * VOC_PJ-863 #3809: 「請求側付帯費用の項目に合わせる」ボタンで使う、
   * 同一フォーム内の請求側付帯費用行の取得関数。渡した呼び出し元 (支払側) だけ
   * ボタンを表示する。押下時にカテゴリのみ支払側へ完全置換コピーする。
   */
  getChargeRowsToCopy?: () => ShipmentOrderAdditionalFormValue[]
}

/**
 * 配送依頼 (ShipmentOrder) の付帯費用テーブル (請求側 / 支払側共通)。
 *
 * - `useFieldArray` で行管理 (`insert` / `remove`)
 * - 末尾合計は `useWatch` で現在値を購読し、`pricingMode='AMOUNT'` の行のみ amount を reduce
 *   (`useFieldArray` の `fields` は値変更で更新されないため購読には使えない)
 * - `+付帯費用を追加` ボタンで空行追加 (カテゴリ未選択 → 保存時バリデーション)
 *
 * #3938 派生のレイアウト崩れ対策: 変更頻度の低い 税区分・内税・切り上げ切り捨て
 * (端数処理) は各行の開閉トグルで主行直下へ縦展開する「行単位アコーディオン」。
 * 主行は [開閉トグル][カテゴリ][運賃込み][金額][備考][削除] のみで横幅に収まる
 * (展開しても横幅を増やさない)。詳細描画は ShipmentOrderAdditionalRow 側にある。
 *
 * shimizu-pg APPROVED 指摘 1 反映:
 *   `pricingMode` 2 状態 (AMOUNT/INCLUDED) を各行で切り替え。
 */
export const ShipmentOrderAdditionalsSection: FC<Props> = ({
  fieldArrayName,
  title,
  totalLabel,
  isEditing,
  tenantKey,
  getChargeRowsToCopy,
  className,
  ...rest
}) => {
  const { control } =
    useFormContext<Record<string, ShipmentOrderAdditionalFormValue[]>>()
  const { fields, append, remove, replace } = useFieldArray({
    control,
    // `useFieldArray` の name は `string` に narrow できないので as any でキャスト
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    name: fieldArrayName as any,
    keyName: 'rhfId'
  })

  // 合計は現在値を購読して再計算する (fields は入力変更では更新されないため)
  const watchedRows = useWatch({
    control,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    name: fieldArrayName as any
  }) as ShipmentOrderAdditionalFormValue[] | undefined

  const handleAdd = useCallback(() => {
    append(createInitialShipmentOrderAdditionalFormValue())
  }, [append])

  // VOC_PJ-863 #3809: コピー元カテゴリの税区分・内税・端数処理・運賃込みは、
  // カテゴリ手動選択時の pre-fill (ShipmentOrderAdditionalRow) と同じ供給源に揃える。
  // getChargeRowsToCopy を渡さない呼び出し元 (請求側自身、対象外の他呼び出し元) では
  // ボタン自体を出さないため取得も止める。ShipmentOrderAdditionalRow 側の行単位フェッチと
  // queryKey (tenantKey, reportType) が同一のため、React Query のキャッシュ共有により
  // 追加のネットワークリクエストは発生しない (意図的な重複呼び出し)。
  const { data: additionalPaymentCategories } =
    useAdditionalPaymentCategoriesSelectQuery(tenantKey, REPORT_TYPE.DAILY, {
      disabled: !getChargeRowsToCopy
    })

  const handleCopyFromCharges = useCallback(() => {
    if (!getChargeRowsToCopy) return
    const categoryIds = Array.from(
      new Set(
        getChargeRowsToCopy()
          .map((row) => row.additionalPaymentCategoryId)
          .filter((id): id is number => id !== null)
      )
    )
    replace(
      buildCopiedAdditionalPaymentFormValue(
        categoryIds,
        additionalPaymentCategories
      )
    )
  }, [getChargeRowsToCopy, additionalPaymentCategories, replace])

  const totalAmount = sumShipmentOrderAdditionalAmounts(watchedRows ?? [])

  // テーブル総列数: カテゴリ/運賃込み/金額/備考 (4) + 開閉トグル (1) + 編集時のみ削除 (1)。
  // 空行・展開行の colSpan / colgroup と一致させる (#3938 行単位アコーディオン)。
  const columnCount = 5 + (isEditing ? 1 : 0)

  return (
    <div
      {...rest}
      className={clsx(['mt-4', className])}
      data-testid={`shipment-order-additionals-${fieldArrayName}`}
    >
      <div className="mb-1 flex items-center justify-between gap-2">
        {/*
          #3743 視認性改善: 付帯費用見出しを配車/配送依頼モーダルの既存仕様
          (BasicTh 相当: font-bold text-secondary, 継承フォントサイズ) に統一する。
          運行実績由来の Title (text-base + bg-primary の青い縦線) はモーダル内で
          他項目より目立ち、既存仕様にない青色の強調オブジェクトも付くため、
          青線を持たないプレーンな見出しへ置換した。
        */}
        <span className="font-bold text-secondary">{title}</span>
        <div className="flex items-center gap-2">
          {isEditing && getChargeRowsToCopy && (
            <Tooltip title={DESCRIPTIONS.MATCH_TO_ADDITIONAL_CHARGES}>
              <Button
                onClick={handleCopyFromCharges}
                type="default"
                data-testid={`${fieldArrayName}-match-to-additional-charges`}
              >
                {ACTIONS.MATCH_TO_ADDITIONAL_CHARGES}
              </Button>
            </Tooltip>
          )}
          {isEditing && (
            <Button onClick={handleAdd} className="me-2" type="default">
              {ACTIONS.ADD}
            </Button>
          )}
        </div>
      </div>
      <Table className="w-full table-fixed">
        {/*
          列構成 (#3938 行単位アコーディオン、左端=開閉トグル):
          [開閉トグル] [カテゴリ] [運賃込み] [金額] [備考] [削除]
          税区分・内税・切り上げ切り捨て (端数処理) は主行に列を持たず、
          開いた行の直下に縦展開する (ShipmentOrderAdditionalRow)。
        */}
        {/*
          #1857 指摘-7: 備考列が狭すぎたため、チェックボックス/金額/削除列の固定幅を
          詰めて備考列 (flex) に幅を回す。狭いテーブル (配車情報の編集モーダルの
          カード内 colSpan セル) でも備考が実用的な幅になるようにする。
        */}
        <colgroup>
          {/* 開閉トグル (左端): 税区分・内税・切り上げ切り捨ての展開ボタン (▸/▾) ぶん (#3938 派生) */}
          <col className="w-[44px]" />
          {/*
            カテゴリ: AdditionalPaymentCategoriesSelect が min-w-[160px] を持つため、
            セルの padding (p-2=16px) を含め 180px 確保しないと Select が隣の
            「運賃込み」セルへ食い込む (#3541 指摘 2026-06-02)。
          */}
          <col className="w-[180px]" />
          {/* 運賃込み: ラベル折返し回避のため 100px (中身は text-right で金額側に寄せる) */}
          <col className="w-[100px]" />
          {/* 金額 */}
          <col className="w-[110px]" />
          {/* 備考 (flex) */}
          <col />
          {/*
            削除: 「削除」ボタン (約60px) + セル padding (p-2=16px) を収容しないと
            text-right の右 padding が潰れてボタンがヘッダの「追加」ボタン (me-2=8px) と
            縦位置がずれる (#3541 指摘 2026-06-02)。80px 確保して両ボタンの右端を揃える。
            閲覧モード (isEditing=false) では削除セル自体を描画しないため、列定義も
            合わせて出し分ける。
          */}
          {isEditing && <col className="w-[80px]" />}
        </colgroup>
        <Tbody>
          {fields.length === 0 && (
            // VOC_PJ-875 #3844: セクション末尾行 (行0件時はこの空行) の既定 border-b を
            // 打ち消す。このセクションを border-b-0 ラッパー直下に置く利用箇所では、
            // この末尾 border-b が次行 (計上日など) との間に区切り線として残ってしまうため、
            // 末尾行を border-b-0 にしてセクションと次行を一塊表示にする。
            <Tr className="border-b-0">
              <Td
                // 列数は columnCount に揃える (#3938 行単位アコーディオン)。
                colSpan={columnCount}
                className="p-2 text-left text-secondary"
              >
                {/* 空表示: 配車板の他のテーブルと同じく汎用 "-" を採用 */}-
              </Td>
            </Tr>
          )}
          {fields.map((field, index) => (
            <ShipmentOrderAdditionalRow
              key={field.rhfId}
              fieldArrayName={fieldArrayName}
              rhfId={field.rhfId}
              index={index}
              tenantKey={tenantKey}
              isEditing={isEditing}
              columnCount={columnCount}
              onRemove={remove}
            />
          ))}
          {fields.length > 0 && (
            // VOC_PJ-875 #3844: 末尾の合計行。border-b-0 ラッパー直下に置く利用箇所では、
            // この合計行の既定 border-b が次行 (計上日など) との区切り線として残るため、
            // border-b-0 にしてセクションと次行を一塊表示にする。
            <Tr className="border-b-0">
              {/* 開閉トグル (左端) */}
              <Td />
              {/* カテゴリ位置: 合計ラベル */}
              <Th className="p-1 text-left font-bold text-secondary">
                {totalLabel}
              </Th>
              {/* 運賃込み */}
              <Td />
              {/* 金額: 合計額 */}
              <Td className="p-1 text-right text-base font-bold">
                <NumberFormatter value={totalAmount} showTriangleForNegative />
                <UnitLabel>{UNITS.CURRENCY.YEN}</UnitLabel>
              </Td>
              {/* 備考 */}
              <Td />
              {/* 削除 (編集時のみ) */}
              {isEditing && <Td />}
            </Tr>
          )}
        </Tbody>
      </Table>
    </div>
  )
}
